import React, { useState, useEffect, useRef } from 'react';
import './QuestionCard.css';
import { useTranslation } from 'react-i18next';
import { getVideoLessonQuestions, transformBackendQuestions, submitQuestionAnswer } from '../../../lib/questionsApi';
import { trackLessonOpened, trackLessonCompleted } from '../../../lib/analytics';

const QuestionCard = ({
    questions = [],
    onTestComplete = () => { },
    maxAttempts = 2,
    enabledTestTypes = { yesNo: true, textVoice: true, dragDrop: true },
    videoLessonId = null, // New prop for backend integration
    onActivityComplete = null // New prop for completion checking
}) => {
    // State management
    const [currentQuestion, setCurrentQuestion] = useState(0);
    const [attempts, setAttempts] = useState(0);
    const [testQuestions, setTestQuestions] = useState([]);
    const [answers, setAnswers] = useState({});
    const [questionResults, setQuestionResults] = useState({});
    const [isRecording, setIsRecording] = useState(false);
    const [testStarted, setTestStarted] = useState(false);
    const [isReviewMode, setIsReviewMode] = useState(false);
    const [showProgressPanel, setShowProgressPanel] = useState(false);
    const [showLandingScreen, setShowLandingScreen] = useState(true);
    const [showQuestionContainer, setShowQuestionContainer] = useState(false);
    const [testTypes, setTestTypes] = useState({ yesNo: true, textVoice: true, dragDrop: true });
    const [testCompleted, setTestCompleted] = useState(false);
    const [showingResults, setShowingResults] = useState(false);
    const [isLoadingQuestions, setIsLoadingQuestions] = useState(false);
    const [backendQuestions, setBackendQuestions] = useState([]);
    const [backendFeedback, setBackendFeedback] = useState({}); // Store AI feedback from backend
    const [showSelectOneType, setShowSelectOneType] = useState(false); // Show test type selection if multiple types available from backend
    const [isClosingModal, setIsClosingModal] = useState(false); // Track modal closing animation
    const { t, i18n } = useTranslation();

    // Refs
    const mediaRecorderRef = useRef(null);
    const streamRef = useRef(null);
    const isManualStopRef = useRef(false);

    // Sample questions data (fallback if no questions provided)
    const sampleQuestions = {
        yesNo: [
            {
                text: "The article mentions that climate change affects polar bears.",
                correctAnswer: "yes",
                hint: "Think about the environmental impacts discussed in the text.",
                explanation: {
                    yes: "Correct! The article specifically discusses how melting ice caps due to climate change are threatening polar bear habitats.",
                    no: "Actually, the article does mention climate change's impact on polar bears. Look for the section about Arctic wildlife and melting ice caps."
                }
            },
            {
                text: "The main character in the story lives in New York.",
                correctAnswer: "no",
                hint: "Pay attention to the city mentioned in the opening paragraph.",
                explanation: {
                    yes: "Not quite. The story mentions that the main character lives in Chicago, not New York. The opening paragraph describes the Chicago skyline.",
                    no: "That's right! The main character lives in Chicago, as mentioned in the opening paragraph's description of the city skyline."
                }
            }
        ],
        textVoice: [
            {
                text: "What is the main theme of the passage?",
                hint: "Consider the recurring ideas throughout the text.",
                sampleAnswer: "environmental protection",
                explanation: "The main theme is environmental protection. Look for recurring mentions of conservation, sustainability, and protecting natural resources throughout the passage."
            },
            {
                text: "Describe the protagonist's motivation in your own words.",
                hint: "Think about what drives the character's actions.",
                sampleAnswer: "desire for adventure",
                explanation: "The protagonist is primarily motivated by a desire for adventure and discovery. Notice how they repeatedly seek new experiences and unknown territories."
            }
        ],
        dragDrop: [
            {
                sentence: "The scientist ___ the experiment ___ careful observation and ___ the results.",
                words: ["conducted", "with", "documented", "through", "analyzed"],
                correctPositions: ["conducted", "with", "documented"],
                hint: "Think about the logical sequence of scientific research.",
                explanation: "The correct sequence is: 'conducted' (performed the experiment), 'with' (the method used), and 'documented' (recorded the results). This follows the natural flow of scientific methodology."
            },
            {
                sentence: "She decided to ___ her career and ___ a new path in ___ field.",
                words: ["change", "pursue", "different", "modify", "another"],
                correctPositions: ["change", "pursue", "another"],
                hint: "Consider common phrases about career changes.",
                explanation: "The correct words are: 'change' (to alter her career), 'pursue' (to follow), and 'another' (a different field). 'Change career' and 'pursue a path' are common English expressions."
            }
        ]
    };

    // Initialize questions only when provided externally (not from sample questions)
    useEffect(() => {
        if (questions.length > 0) {
            setTestQuestions(questions.slice(0, 5));
        }
        // Do NOT initialize sample questions here - only when user starts testing
    }, [questions]);

    // Re-render content when language changes or answers change
    useEffect(() => {
        if (testStarted && showQuestionContainer && testQuestions.length > 0 && !isReviewMode && !showingResults) {
            // Re-render current question when language changes or answers change
            showQuestion(currentQuestion);
        } else if (showingResults) {
            // Re-render results when language changes
            const correctCount = Object.values(questionResults).filter(r => r === 'correct').length;
            const partialCount = Object.values(questionResults).filter(r => r === 'partial').length;
            const incorrectCount = testQuestions.length - correctCount - partialCount;
            const percentage = Math.round((correctCount / testQuestions.length) * 100);
            displayResults(correctCount, partialCount, incorrectCount, percentage);
        } else if (isReviewMode) {
            // Re-render review mode when language changes
            showQuestionReview(currentQuestion);
        }
    }, [i18n.language, testStarted, showQuestionContainer, testQuestions.length, isReviewMode, showingResults, currentQuestion, questionResults, answers]);
    // Show question function (matches HTML version)
    const showQuestion = (index) => {
        const container = document.getElementById('questionContainer');
        const question = testQuestions[index];

        // Only show questions if we have properly initialized testQuestions and the test has started
        if (!container || !question || !testStarted || testQuestions.length === 0) return;

        // Scroll to top of container
        window.scrollTo({ top: 0, behavior: 'smooth' });

        // Update active circle
        for (let i = 1; i <= testQuestions.length; i++) {
            const circle = document.getElementById(`circle${i}`);
            if (circle) {
                circle.classList.remove('active');
            }
        }
        const activeCircle = document.getElementById(`circle${index + 1}`);
        if (activeCircle) {
            activeCircle.classList.add('active');
        }

        // Get localized strings
        const questionText = t("Question");
        const hintText = t("Hint");
        const ofText = t("of");

        // Only show hint button if hint exists and is not empty
        const showHintButton = question.hint && question.hint.trim() !== '';

        let questionHTML = `
      <div class="question-card">
        <div class="question-header">
          <div class="question-number">${questionText} ${index + 1} ${ofText} ${testQuestions.length}</div>
          ${showHintButton ? `<button class="hint-btn" onclick="window.toggleHint(${index})">💡 ${hintText}</button>` : ''}
        </div>
        ${showHintButton ? `<div class="hint-content" id="hint${index}">${question.hint}</div>` : ''}
    `;

        if (question.type === 'yesNo') {
            const isAnswered = questionResults[index];
            const userAnswer = answers[index];

            const blwAnswerText = t("Select your answer – it will be checked automatically");
            const YES = t("YES");
            const NO = t("NO")
            questionHTML += `
        <p style="font-size: 1.2em; margin-bottom: 20px; color: #1f2937;">${question.text}</p>
        <p style="font-size: 0.9em; color: #6b7280; margin-bottom: 20px; text-align: center;">
          <em>${blwAnswerText}</em>
        </p>
        <div class="yes-no-container">
          <div class="yes-no-option">
            <input type="radio" id="yes${index}" name="q${index}" value="yes" 
                   ${userAnswer === 'yes' ? 'checked' : ''} 
                   ${isAnswered ? 'disabled' : ''} 
                   onchange="window.instantCheckYesNo(${index})">
            <label for="yes${index}">${YES}</label>
          </div>
          <div class="yes-no-option">
            <input type="radio" id="no${index}" name="q${index}" value="no" 
                   ${userAnswer === 'no' ? 'checked' : ''} 
                   ${isAnswered ? 'disabled' : ''} 
                   onchange="window.instantCheckYesNo(${index})">
            <label for="no${index}">${NO}</label>
          </div>
        </div>
        <div class="explanation-section" id="explanation${index}"></div>
      `;
        } else if (question.type === 'textVoice') {
            const isAnswered = questionResults[index];
            const userAnswer = answers[index] || '';

            const AnswerByTextVoice = t("Answer by Text or Voice");
            const TextVoiceTxtBlw = t("Type your answer below or click the microphone to record your voice");
            const textareaPlaceholder = t("Type your answer here…");
            const ready = t("Ready");
            const answered = t("Answered")
            questionHTML += `
        <p style="font-size: 1.2em; margin-bottom: 20px; color: #1f2937;">${question.text}</p>
        <div class="voice-input-section">
          <div class="voice-input-header">
            <h3>${AnswerByTextVoice}</h3>
            <p>${TextVoiceTxtBlw}</p>
          </div>
          <div class="input-container" ${isAnswered ? 'data-readonly="true"' : ''}>
            <textarea class="text-input-field" id="textAnswer${index}" 
                   placeholder="${textareaPlaceholder}" 
                   rows="1"
                   ${isAnswered ? 'readonly' : ''}>${userAnswer}</textarea>
            <div class="voice-controls">
              <span class="voice-status" id="voiceStatus${index}">${isAnswered ? answered : ready}</span>
              <button class="voice-record-btn" id="voiceBtn${index}" 
                      onclick="window.toggleRecording(${index})" 
                      ${isAnswered ? 'style="display:none;"' : ''}>
                🎤
              </button>
              ${!isAnswered ? `
                <button class="voice-clear-btn" id="clearBtn${index}" onclick="window.clearVoiceInput(${index})" style="display:none;">
                  🗑️
                </button>
              ` : ''}
            </div>
          </div>
        </div>
        <div class="explanation-section" id="explanation${index}"></div>
      `;
        } else if (question.type === 'dragDrop') {
            const isAnswered = questionResults[index];
            const userAnswer = answers[index] || {};

            // Handle both old format (array) and new format (object with gap mappings)
            let displayAnswer = userAnswer;
            if (Array.isArray(userAnswer)) {
                // Convert old array format to new object format for display
                displayAnswer = {};
                userAnswer.forEach((word, i) => {
                    displayAnswer[`g${i + 1}`] = word;
                });
            }

            const dragDropMessage = t("Drag the words to complete the sentence:");
            const dragDropMessageP = t("Your answer will be checked automatically when all gaps are filled");

            // Build sentence HTML from template segments
            let sentenceHTML = '';
            let gapIndex = 0;

            if (question.templateSegments) {
                // New backend format with template segments
                question.templateSegments.forEach((segment, segIndex) => {
                    if (segment.type === 'fixed') {
                        sentenceHTML += segment.content;
                    } else if (segment.type === 'gap') {
                        const gapId = segment.gap_id;
                        // Get the actual word content, not the word ID
                        let gapWord = '';
                        if (displayAnswer[gapId]) {
                            // Find the word by ID and get its content
                            const wordObj = question.draggableWords?.find(w => w.word_id === displayAnswer[gapId]);
                            gapWord = wordObj ? wordObj.content : displayAnswer[gapId];
                        }
                        const gapClass = gapWord ? 'filled' : '';
                        const dropHandlers = isAnswered ? '' : `ondrop="window.dropAndCheckWordOrder(event, ${index}, '${gapId}')" ondragover="window.allowDrop(event)" ondragleave="window.removeDragOver(event)"`;
                        const clickHandler = isAnswered ? '' : `onclick="window.returnWordToBankWordOrder(${index}, '${gapId}')"`;

                        // Make filled gaps draggable so users can move words between gaps
                        const dragHandlersForGap = (gapWord && !isAnswered) ? `draggable="true" ondragstart="window.dragWordOrder(event)"` : '';
                        
                        sentenceHTML += `<span class="word-gap ${gapClass}" id="gap${index}_${gapId}" data-word-id="${displayAnswer[gapId] || ''}" data-word="${gapWord}" ${dropHandlers} ${clickHandler} ${dragHandlersForGap}>${gapWord}</span>`;
                        gapIndex++;
                    }
                    if (segIndex < question.templateSegments.length - 1) {
                        sentenceHTML += ' ';
                    }
                });
            } else {
                // Fallback to old format
                const gaps = question.sentence.split('___');
                sentenceHTML = gaps[0];

                for (let i = 1; i < gaps.length; i++) {
                    const gapWord = Array.isArray(userAnswer) ? (userAnswer[i - 1] || '') : '';
                    const gapClass = gapWord ? 'filled' : '';
                    const dropHandlers = isAnswered ? '' : `ondrop="window.dropAndCheck(event, ${index}, ${i - 1})" ondragover="window.allowDrop(event)" ondragleave="window.removeDragOver(event)"`;
                    const clickHandler = isAnswered ? '' : `onclick="window.returnWordToBank(${index}, ${i - 1})"`;

                    sentenceHTML += `<span class="word-gap ${gapClass}" id="gap${index}_${i - 1}" ${dropHandlers} ${clickHandler}>${gapWord}</span>${gaps[i]}`;
                }
            }

            questionHTML += `
        <div class="drag-drop-container">
          <p style="font-size: 1.1em; color: #6b7280; margin-bottom: 10px;">${dragDropMessage}</p>
          <p style="font-size: 0.9em; color: #6b7280; margin-bottom: 20px; text-align: center;">
            <em>${dragDropMessageP}</em>
          </p>
          <div class="sentence-with-gaps">${sentenceHTML}</div>
          <div class="draggable-words">
            ${(question.draggableWords || question.words || []).map((wordObj, i) => {
                const word = typeof wordObj === 'string' ? wordObj : wordObj.content;
                const wordId = typeof wordObj === 'string' ? `w${i + 1}` : wordObj.word_id;
                // Check if this word is currently used in any gap
                const isUsed = Object.values(displayAnswer).includes(wordId);
                const dragHandlers = (isAnswered || isUsed) ? 'draggable="false"' : `draggable="true" ondragstart="window.dragWordOrder(event)"`;
                const usedClass = isUsed ? 'used' : '';
                const styleAttr = isUsed ? 'style="opacity: 0.5; pointer-events: none;"' : '';

                return `
                  <div class="draggable-word ${usedClass}" id="word${index}_${i}" ${dragHandlers} ${styleAttr} data-word="${word}" data-word-id="${wordId}">
                    ${word}
                  </div>
                `;
            }).join('')}
          </div>
        </div>
        <div class="explanation-section" id="explanation${index}"></div>
      `;
        }

        // Only add action buttons for text/voice questions if not yet answered
        const checkAnswer = t("Check Answer");

        if (question.type === 'textVoice' && !questionResults[index]) {
            questionHTML += `
        </div>
        <div class="action-buttons">
          <button class="action-btn check-btn" onclick="window.checkAnswer(${index})">${checkAnswer}</button>
        </div>
      `;
        } else {
            questionHTML += `</div>`;
        }

        container.innerHTML = questionHTML;

        // Auto-resize any textareas that were just created
        setTimeout(() => {
            const textarea = document.getElementById(`textAnswer${index}`);
            if (textarea && textarea.tagName.toLowerCase() === 'textarea') {
                autoResizeTextarea(textarea);
            }
        }, 0);

        // If question is already answered, show the explanation
        if (questionResults[index]) {
            const isCorrect = questionResults[index] === 'correct';
            const isPartial = questionResults[index] === 'partial';
            showExplanation(index, isCorrect, isPartial, answers[index]);
        }
    };
    const startTesting = async () => {
        if (!testTypes.yesNo && !testTypes.textVoice && !testTypes.dragDrop) {
            setShowSelectOneType(true);
            return;
        }

        let finalQuestions = [];

        // Try to fetch questions from backend if videoLessonId is provided
        if (videoLessonId && backendQuestions.length === 0) {
            setIsLoadingQuestions(true);
            try {
                let questionsByType = {};
                
                // Determine which question types to fetch based on selected test types
                const questionTypesToFetch = [];
                if (testTypes.yesNo) questionTypesToFetch.push("yes_no");
                if (testTypes.textVoice) questionTypesToFetch.push("open_ended");
                if (testTypes.dragDrop) questionTypesToFetch.push("word_order");

                // If multiple types are selected, fetch each type separately and store by type
                if (questionTypesToFetch.length > 1) {
                    console.log('Fetching multiple question types:', questionTypesToFetch);
                    
                    // Fetch each question type separately
                    for (const questionType of questionTypesToFetch) {
                        try {
                            const questionsData = await getVideoLessonQuestions(videoLessonId, questionType);
                            const transformedQuestions = transformBackendQuestions(questionsData.questions || []);
                            
                            // Group by frontend type
                            transformedQuestions.forEach(q => {
                                if (!questionsByType[q.type]) {
                                    questionsByType[q.type] = [];
                                }
                                questionsByType[q.type].push(q);
                            });
                        } catch (error) {
                            console.warn(`Failed to fetch ${questionType} questions:`, error);
                            // Continue with other question types even if one fails
                        }
                    }
                } else if (questionTypesToFetch.length === 1) {
                    // Single question type - fetch directly
                    const questionType = questionTypesToFetch[0];
                    console.log('Fetching single question type:', questionType);
                    
                    const questionsData = await getVideoLessonQuestions(videoLessonId, questionType);
                    const transformedQuestions = transformBackendQuestions(questionsData.questions || []);
                    
                    // Group by frontend type
                    transformedQuestions.forEach(q => {
                        if (!questionsByType[q.type]) {
                            questionsByType[q.type] = [];
                        }
                        questionsByType[q.type].push(q);
                    });
                } else {
                    // No question types selected - this shouldn't happen due to earlier check
                    throw new Error('No question types selected');
                }

                console.log('Questions by type:', questionsByType);

                // Distribute questions evenly across selected types
                finalQuestions = distributeQuestions(questionsByType, testTypes);

                // Store all fetched questions for potential reuse
                const allQuestions = Object.values(questionsByType).flat();
                setBackendQuestions(allQuestions);
                
            } catch (error) {
                console.error('Failed to fetch backend questions:', error);
                // Fall back to sample questions
                finalQuestions = getFallbackQuestions();
            } finally {
                setIsLoadingQuestions(false);
            }
        } else if (backendQuestions.length > 0) {
            // Use already loaded backend questions - redistribute them
            const questionsByType = {};
            backendQuestions.forEach(q => {
                if (!questionsByType[q.type]) {
                    questionsByType[q.type] = [];
                }
                questionsByType[q.type].push(q);
            });
            
            finalQuestions = distributeQuestions(questionsByType, testTypes);
        } else {
            // Use sample questions as fallback
            finalQuestions = getFallbackQuestions();
        }

        if (finalQuestions.length === 0) {
            alert(t('No questions available!'));
            return;
        }

        // Console log all questions and their correct answers for debugging
        console.log('=== QUESTION CARD DEBUG ===');
        console.log('Final questions to be used:', finalQuestions);
        finalQuestions.forEach((q, index) => {
            console.log(`Question ${index + 1}:`, {
                type: q.type,
                text: q.text,
                correctAnswer: q.correctAnswer,
                sampleAnswer: q.sampleAnswer,
                correctPositions: q.correctPositions,
                id: q.id
            });
        });
        console.log('=== END DEBUG ===');

        // Update testQuestions with selected questions
        setTestQuestions(finalQuestions);

        const newAttempts = attempts + 1;
        setAttempts(newAttempts);
        setTestStarted(true);
        setShowProgressPanel(true);
        setShowLandingScreen(false);
        setShowQuestionContainer(true);
        setCurrentQuestion(0);
        setAnswers({});
        setQuestionResults({});
        setBackendFeedback({}); // Clear backend feedback for new test
        setTestCompleted(false);
        setShowingResults(false);

        resetProgressCircles();
        updateProgressLine();

        // Also ensure the container is visible via direct DOM manipulation as backup
        setTimeout(() => {
            const container = document.getElementById('questionContainer');
            if (container) {
                container.style.display = 'block';
            }
            showQuestion(0);
        }, 100);
    };

    // Helper function to distribute questions evenly across selected types
    const distributeQuestions = (questionsByType, selectedTypes) => {
        const totalQuestions = 5;
        const selectedTypesList = [];
        
        // Build list of selected types in difficulty order (easiest to hardest)
        if (selectedTypes.yesNo && questionsByType.yesNo?.length > 0) {
            selectedTypesList.push('yesNo');
        }
        if (selectedTypes.textVoice && questionsByType.textVoice?.length > 0) {
            selectedTypesList.push('textVoice');
        }
        if (selectedTypes.dragDrop && questionsByType.dragDrop?.length > 0) {
            selectedTypesList.push('dragDrop');
        }

        if (selectedTypesList.length === 0) {
            return [];
        }

        const distributedQuestions = [];
        
        if (selectedTypesList.length === 1) {
            // Only one type selected - take all 5 from that type
            const type = selectedTypesList[0];
            const questions = questionsByType[type] || [];
            distributedQuestions.push(...questions.slice(0, totalQuestions));
        } else if (selectedTypesList.length === 2) {
            // Two types selected - 3 of easier type, 2 of harder type
            const easierType = selectedTypesList[0];
            const harderType = selectedTypesList[1];
            
            const easierQuestions = questionsByType[easierType] || [];
            const harderQuestions = questionsByType[harderType] || [];
            
            distributedQuestions.push(...easierQuestions.slice(0, 3));
            distributedQuestions.push(...harderQuestions.slice(0, 2));
        } else if (selectedTypesList.length === 3) {
            // All three types selected - 2 yes/no, 2 text/voice, 1 drag/drop
            const yesNoQuestions = questionsByType.yesNo || [];
            const textVoiceQuestions = questionsByType.textVoice || [];
            const dragDropQuestions = questionsByType.dragDrop || [];
            
            distributedQuestions.push(...yesNoQuestions.slice(0, 2));
            distributedQuestions.push(...textVoiceQuestions.slice(0, 2));
            distributedQuestions.push(...dragDropQuestions.slice(0, 1));
        }

        // If we don't have enough questions, fill with what we have
        if (distributedQuestions.length < totalQuestions) {
            const allAvailableQuestions = Object.values(questionsByType).flat();
            const usedIds = new Set(distributedQuestions.map(q => q.id));
            
            for (const question of allAvailableQuestions) {
                if (distributedQuestions.length >= totalQuestions) break;
                if (!usedIds.has(question.id)) {
                    distributedQuestions.push(question);
                    usedIds.add(question.id);
                }
            }
        }

        console.log('Question distribution:', {
            selectedTypes: selectedTypesList,
            distribution: distributedQuestions.map(q => q.type),
            totalCount: distributedQuestions.length
        });

        return distributedQuestions.slice(0, totalQuestions);
    };

    // Helper function to get fallback sample questions
    const getFallbackQuestions = () => {
        const questionsByType = {};
        
        if (testTypes.yesNo) {
            questionsByType.yesNo = sampleQuestions.yesNo.map(q => ({ ...q, type: 'yesNo' }));
        }
        if (testTypes.textVoice) {
            questionsByType.textVoice = sampleQuestions.textVoice.map(q => ({ ...q, type: 'textVoice' }));
        }
        if (testTypes.dragDrop) {
            questionsByType.dragDrop = sampleQuestions.dragDrop.map(q => ({ ...q, type: 'dragDrop' }));
        }
        
        return distributeQuestions(questionsByType, testTypes);
    };

    const resetProgressCircles = () => {
        for (let i = 1; i <= testQuestions.length; i++) {
            const circle = document.getElementById(`circle${i}`);
            if (circle) {
                circle.classList.remove('completed', 'incorrect', 'partial', 'active');
            }
        }
    };

    const goToQuestion = (index) => {
        if (!testStarted || index >= testQuestions.length) return;

        // Hide results when navigating to questions
        setShowingResults(false);

        if (isReviewMode) {
            showQuestionReview(index);
        } else {
            setCurrentQuestion(index);
            showQuestion(index);
        }
    };

    const updateProgressLine = () => {
        const completedCount = Object.keys(questionResults).length;
        const progressPercentage = (completedCount / testQuestions.length) * 100;
        const progressLine = document.querySelector('.progress-line');
        if (progressLine) {
            progressLine.style.setProperty('--progress', `${progressPercentage}%`);
        }
    };

    const toggleHint = (index) => {
        const hintContent = document.getElementById(`hint${index}`);
        if (hintContent) {
            hintContent.style.display = hintContent.style.display === 'none' || !hintContent.style.display ? 'block' : 'none';
        }
    };

    const showExplanation = (index, isCorrect, isPartial, userAnswer, isReview = false) => {
        const question = testQuestions[index];
        const explanationDiv = document.getElementById(`explanation${index}`);

        if (!explanationDiv) return;

        // Don't show explanation for correct answers (unless in review mode)
        if (isCorrect && !isReview) {
            return;
        }

        let titleText, iconEmoji, explanationText, className;
        const yourAnswerT = t("Your answer:");

        // Check if we have backend feedback for this question
        const hasBackendFeedback = backendFeedback[index];

        // For backend questions, use the backend's answer_status as the source of truth
        // The isCorrect parameter comes from answer_status: "RIGHT" or "WRONG"
        let actuallyCorrect = isCorrect;

        if (actuallyCorrect) {
            titleText = `${t("Correct")}!`;
            iconEmoji = "✅";
            className = "correct";

            if (hasBackendFeedback) {
                explanationText = backendFeedback[index];
            } else if (question.type === 'yesNo') {
                explanationText = question.explanation[userAnswer];
            } else {
                explanationText = "Great job! Your answer is correct.";
            }
        } else if (isPartial) {
            titleText = t("Partially Correct");
            iconEmoji = "⚠️";
            className = "partial";

            if (hasBackendFeedback) {
                explanationText = backendFeedback[index];
            } else {
                explanationText = question.explanation || "You're on the right track, but not quite there yet.";
            }
        } else {
            titleText = t("Incorrect");
            iconEmoji = "❌";
            className = "incorrect";

            if (hasBackendFeedback) {
                // Use AI feedback from backend
                explanationText = backendFeedback[index];
            } else if (question.type === 'yesNo') {
                explanationText = question.explanation[userAnswer];
            } else if (question.type === 'dragDrop') {
                explanationText = question.explanation;

                // Handle different answer formats for display
                if (typeof userAnswer === 'object' && !Array.isArray(userAnswer)) {
                    // New word order format - show actual words placed by user
                    const userWords = [];
                    const correctAnswer = t("Correct answer:");
                    
                    // Get the words the user placed in order
                    if (question.templateSegments) {
                        question.templateSegments.forEach(segment => {
                            if (segment.type === 'gap') {
                                const wordId = userAnswer[segment.gap_id];
                                if (wordId) {
                                    const wordObj = question.draggableWords?.find(w => w.word_id === wordId);
                                    userWords.push(wordObj ? wordObj.content : wordId);
                                } else {
                                    userWords.push('___');
                                }
                            }
                        });
                    }
                    
                    explanationText += `<div class="your-answer"><strong>${yourAnswerT}</strong> ${userWords.join(' • ')}<br><strong>${correctAnswer}</strong> See feedback above</div>`;
                } else if (Array.isArray(userAnswer)) {
                    // Old drag & drop format
                    const gaps = userAnswer;
                    const correctAnswer = t("Correct answer:");
                    explanationText += `<div class="your-answer"><strong>${yourAnswerT}</strong> "${gaps.join(' ___ ')}"<br><strong>${correctAnswer}</strong> "${question.correctPositions ? question.correctPositions.join(' ___ ') : 'See feedback above'}"</div>`;
                }
            } else {
                explanationText = question.explanation;
                explanationText += `<div class="your-answer"><strong>${yourAnswerT}</strong> "${userAnswer}"</div>`;
            }
        }

        explanationDiv.innerHTML = `
      <div class="explanation-title ${className}">
        <span>${iconEmoji}</span>
        <span>${titleText}</span>
      </div>
      <div class="explanation-text">${explanationText}</div>
    `;

        // Add next/finish button for incorrect or partial answers (not in review mode)
        if (!isCorrect && !isReview) {
            // Check if this is the last unanswered question
            const nextUnanswered = findNextUnansweredQuestion();
            const isLastQuestion = nextUnanswered === -1;

            const buttonText = isLastQuestion ? t("Finish Test") : t("Next Question");
            const buttonIcon = isLastQuestion ? "✓" : "→";

            explanationDiv.innerHTML += `
        <div class="explanation-actions">
          <button class="action-btn next-btn" onclick="window.nextQuestion()">
            ${buttonText} ${buttonIcon}
          </button>
        </div>
      `;
        }

        explanationDiv.className = `explanation-section ${className}`;
        explanationDiv.style.display = 'block';
    };

    const instantCheckYesNo = async (index) => {
        // Prevent re-answering if question is already answered
        if (questionResults[index]) {
            return;
        }

        const question = testQuestions[index];
        const selected = document.querySelector(`input[name="q${index}"]:checked`);

        if (selected) {
            console.log('Checking Yes/No answer:', {
                questionIndex: index,
                userAnswer: selected.value,
                questionCorrectAnswer: question.correctAnswer,
                questionId: question.id,
                isBackendQuestion: !!question.id
            });

            // Disable inputs immediately to prevent multiple submissions
            document.querySelectorAll(`input[name="q${index}"]`).forEach(input => {
                input.disabled = true;
            });

            let isCorrect = false;
            let backendFeedback = null;

            if (question.id) {
                // Backend question - validate with backend
                try {
                    console.log('Submitting answer to backend for validation...');
                    
                    // Track first lesson opened when user submits first answer
                    if (videoLessonId) {
                        trackLessonOpened(videoLessonId);
                    }
                    
                    const validationResult = await submitQuestionAnswer(question.id, selected.value, 'yesNo');
                    console.log('Backend validation result:', validationResult);

                    // Backend returns "RIGHT" or "WRONG"
                    isCorrect = validationResult.result === 'RIGHT';
                    backendFeedback = validationResult.feedback;
                } catch (error) {
                    console.error('Error validating answer with backend:', error);
                    // On error, assume incorrect for safety
                    isCorrect = false;
                }
            } else {
                // Sample question with known correct answer
                isCorrect = selected.value === question.correctAnswer;
            }

            const newAnswers = { ...answers, [index]: selected.value };
            const newResults = { ...questionResults, [index]: isCorrect ? 'correct' : 'incorrect' };

            // Store backend feedback if available
            if (backendFeedback) {
                setBackendFeedback(prev => ({ ...prev, [index]: backendFeedback }));
            }

            setAnswers(newAnswers);
            setQuestionResults(newResults);

            const circle = document.getElementById(`circle${index + 1}`);
            if (circle) {
                circle.classList.remove('active');

                if (isCorrect) {
                    circle.classList.add('completed');
                } else {
                    circle.classList.add('incorrect');
                }
            }

            updateProgressLine();
            showExplanation(index, isCorrect, false, selected.value);
            showFeedback(isCorrect, false, isCorrect, newResults);
        }
    };

    const checkAnswer = async (index) => {
        // Prevent re-answering if question is already answered
        if (questionResults[index]) {
            return;
        }

        const question = testQuestions[index];
        let isCorrect = false;
        let isPartial = false;
        let userAnswer = null;

        if (question.type === 'yesNo') {
            const selected = document.querySelector(`input[name="q${index}"]:checked`);
            if (selected) {
                isCorrect = selected.value === question.correctAnswer;
                userAnswer = selected.value;
            }
        } else if (question.type === 'textVoice') {
            const answer = document.getElementById(`textAnswer${index}`)?.value.trim();
            if (answer) {
                console.log('Checking text/voice answer:', {
                    questionIndex: index,
                    userAnswer: answer,
                    questionSampleAnswer: question.sampleAnswer,
                    questionId: question.id,
                    isBackendQuestion: !!question.id
                });

                let backendFeedbackText = null;

                if (question.id) {
                    // Backend question - validate with backend
                    try {
                        console.log('Submitting text/voice answer to backend for validation...');
                        const validationResult = await submitQuestionAnswer(question.id, answer, 'textVoice');
                        console.log('Backend validation result:', validationResult);

                        // Backend returns "RIGHT" or "WRONG"
                        isCorrect = validationResult.result === 'RIGHT';
                        isPartial = false; // Backend doesn't support partial for now
                        backendFeedbackText = validationResult.feedback;
                    } catch (error) {
                        console.error('Error validating text/voice answer with backend:', error);
                        // On error, assume incorrect for safety
                        isCorrect = false;
                        isPartial = false;
                    }
                } else if (question.sampleAnswer) {
                    // Sample question with known sample answer
                    isCorrect = answer.toLowerCase().includes(question.sampleAnswer.split(' ')[0]);
                    isPartial = !isCorrect && answer.length > 5;
                } else {
                    // Fallback - assume correct for now
                    console.log('No validation method available - marking as correct');
                    isCorrect = true;
                    isPartial = false;
                }

                // Store backend feedback if available
                if (backendFeedbackText) {
                    setBackendFeedback(prev => ({ ...prev, [index]: backendFeedbackText }));
                }

                userAnswer = answer;
            }
        } else if (question.type === 'dragDrop') {
            if (question.templateSegments) {
                // New word order format - collect gap_id -> word_id mappings
                const gapMappings = {};
                let totalGaps = 0;
                let filledGaps = 0;
                let backendFeedbackText = null;

                question.templateSegments.forEach(segment => {
                    if (segment.type === 'gap') {
                        totalGaps++;
                        const gap = document.getElementById(`gap${index}_${segment.gap_id}`);
                        const wordId = gap?.getAttribute('data-word-id') || '';
                        if (wordId) {
                            gapMappings[segment.gap_id] = wordId;
                            filledGaps++;
                        }
                    }
                });

                console.log('Word order answer:', {
                    questionIndex: index,
                    gapMappings,
                    questionId: question.id,
                    isBackendQuestion: !!question.id
                });

                if (question.id) {
                    // Backend question - validate with backend
                    try {
                        console.log('Submitting word order answer to backend for validation...');
                        const validationResult = await submitQuestionAnswer(question.id, gapMappings, 'dragDrop');
                        console.log('Backend validation result:', validationResult);

                        isCorrect = validationResult.result === 'RIGHT';
                        isPartial = false; // Backend doesn't support partial for word order yet
                        backendFeedbackText = validationResult.feedback;
                    } catch (error) {
                        console.error('Error validating word order answer with backend:', error);
                        isCorrect = false;
                        isPartial = false;
                    }
                } else {
                    // Fallback for sample questions
                    isCorrect = filledGaps === totalGaps;
                    isPartial = filledGaps > 0 && filledGaps < totalGaps;
                }

                // Store backend feedback if available
                if (backendFeedbackText) {
                    setBackendFeedback(prev => ({ ...prev, [index]: backendFeedbackText }));
                }

                userAnswer = gapMappings;
            } else {
                // Old drag & drop format
                const gaps = [];
                let correctCount = 0;
                for (let i = 0; i < question.correctPositions.length; i++) {
                    const gap = document.getElementById(`gap${index}_${i}`);
                    const word = gap?.textContent.trim() || '';
                    gaps.push(word);
                    if (word === question.correctPositions[i]) {
                        correctCount++;
                    }
                }
                isCorrect = correctCount === question.correctPositions.length;
                isPartial = correctCount > 0 && correctCount < question.correctPositions.length;
                userAnswer = gaps;
            }
        }

        if (!userAnswer) {
            alert(t('Please provide an answer!'));
            return;
        }

        const newAnswers = { ...answers, [index]: userAnswer };
        const newResults = { ...questionResults, [index]: isCorrect ? 'correct' : (isPartial ? 'partial' : 'incorrect') };

        setAnswers(newAnswers);
        setQuestionResults(newResults);

        // Update circle
        const circle = document.getElementById(`circle${index + 1}`);
        if (circle) {
            circle.classList.remove('active');

            if (isCorrect) {
                circle.classList.add('completed');
            } else if (isPartial) {
                circle.classList.add('partial');
            } else {
                circle.classList.add('incorrect');
            }
        }

        updateProgressLine();
        showExplanation(index, isCorrect, isPartial, userAnswer);
        showFeedback(isCorrect, isPartial, isCorrect, newResults);

        // Handle buttons and inputs for text/voice questions
        if (question.type === 'textVoice') {
            const actionButtons = document.querySelector('.action-buttons');
            if (actionButtons) {
                actionButtons.style.display = 'none';
            }
            const input = document.getElementById(`textAnswer${index}`);
            const voiceBtn = document.getElementById(`voiceBtn${index}`);
            const clearBtn = document.getElementById(`clearBtn${index}`);
            const status = document.getElementById(`voiceStatus${index}`);

            if (input) {
                input.readOnly = true;
                input.style.background = '#f3f4f6';
                input.style.cursor = 'not-allowed';
            }
            if (voiceBtn) voiceBtn.style.display = 'none';
            if (clearBtn) clearBtn.style.display = 'none';
            if (status) status.textContent = 'Answered';
        }
    };

    const findNextUnansweredQuestion = (updatedResults = null) => {
        const currentResults = updatedResults || questionResults;
        for (let i = 0; i < testQuestions.length; i++) {
            if (!currentResults[i]) {
                return i;
            }
        }
        return -1; // All questions answered
    };

    const showFeedback = (isCorrect, isPartial, autoAdvance = false, updatedResults = null) => {
        const circle = document.getElementById(`circle${currentQuestion + 1}`);
        if (circle) {
            circle.classList.add('animating');
        }

        if (isCorrect) {
            const successMsg = document.createElement('div');
            successMsg.className = 'success-message';
            successMsg.textContent = `🎉 ${t("Correct")}!`;
            document.body.appendChild(successMsg);

            const colors = ['#10b981', '#667eea', '#f59e0b', '#ec4899', '#f093fb', '#4facfe'];
            const circleRect = circle?.getBoundingClientRect();

            if (circleRect) {
                for (let i = 0; i < 20; i++) {
                    setTimeout(() => {
                        const confetti = document.createElement('div');
                        confetti.className = 'confetti';
                        const angle = (i / 20) * 2 * Math.PI;
                        const distance = 10 + Math.random() * 40;
                        confetti.style.left = circleRect.left + circleRect.width / 2 - 5 + Math.cos(angle) * distance + 'px';
                        confetti.style.top = circleRect.top + circleRect.height / 2 - 5 + Math.sin(angle) * distance + 'px';
                        confetti.style.backgroundColor = colors[Math.floor(Math.random() * colors.length)];
                        const size = 6 + Math.random() * 10;
                        confetti.style.width = size + 'px';
                        confetti.style.height = size + 'px';
                        if (Math.random() > 0.5) {
                            confetti.style.borderRadius = '50%';
                        }
                        document.body.appendChild(confetti);

                        setTimeout(() => confetti.remove(), 2000);
                    }, i * 30);
                }
            }

            setTimeout(() => {
                if (successMsg.parentNode) {
                    successMsg.remove();
                }
            }, 1500);
        }

        setTimeout(() => {
            if (circle) {
                circle.classList.remove('animating');
            }
        }, 800);

        if (autoAdvance && isCorrect) {
            setTimeout(() => {
                // Check if all questions are answered
                const nextUnanswered = findNextUnansweredQuestion(updatedResults);

                if (nextUnanswered === -1) {
                    // All questions answered, show results
                    showResults(updatedResults);
                } else {
                    // Move to next unanswered question
                    setCurrentQuestion(nextUnanswered);
                }
            }, 1800);
        }
    };

    const nextQuestion = () => {
        // Check if all questions are answered first
        const nextUnanswered = findNextUnansweredQuestion();

        if (nextUnanswered === -1) {
            // All questions answered, show results
            showResults();
        } else {
            // Move to next unanswered question
            const container = document.getElementById('questionContainer');
            if (container) {
                container.classList.add('transitioning');

                setTimeout(() => {
                    setCurrentQuestion(nextUnanswered);
                    container.classList.remove('transitioning');
                }, 300);
            }
        }
    };

    const showResults = (updatedResults = null) => {
        // Use the passed results or current state
        const currentResults = updatedResults || questionResults;

        // Debug: Log the questionResults to see what's actually stored
        console.log('questionResults:', currentResults);
        console.log('testQuestions.length:', testQuestions.length);
        console.log('answers:', answers);

        const correctCount = Object.values(currentResults).filter(r => r === 'correct').length;
        const partialCount = Object.values(currentResults).filter(r => r === 'partial').length;
        const incorrectCount = testQuestions.length - correctCount - partialCount;
        const percentage = Math.round((correctCount / testQuestions.length) * 100);

        console.log('correctCount:', correctCount, 'partialCount:', partialCount, 'incorrectCount:', incorrectCount);

        // Mark test as completed and showing results
        setTestCompleted(true);
        setIsReviewMode(false);
        setShowingResults(true);

        // Use setTimeout to ensure state updates are applied before displaying results
        setTimeout(() => {
            displayResults(correctCount, partialCount, incorrectCount, percentage);
        }, 10);
    };

    const generateErrorAnalysis = () => {
        const incorrectQuestions = [];
        const partialQuestions = [];
        const questionT = t("Question");
        const IncorrectAnsw = t("Incorrect Answers");
        const partlyCorrect = t("Partially Correct Answers");

        console.log('=== ERROR ANALYSIS DEBUG ===');
        console.log('testQuestions:', testQuestions);
        console.log('questionResults:', questionResults);
        console.log('answers:', answers);

        testQuestions.forEach((question, index) => {
            console.log(`Processing question ${index}:`, {
                question,
                result: questionResults[index],
                answer: answers[index]
            });

            if (questionResults[index] === 'incorrect') {
                incorrectQuestions.push({ question, index, userAnswer: answers[index] });
            } else if (questionResults[index] === 'partial') {
                partialQuestions.push({ question, index, userAnswer: answers[index] });
            }
        });

        console.log('incorrectQuestions:', incorrectQuestions);
        console.log('partialQuestions:', partialQuestions);
        console.log('=== END ERROR ANALYSIS DEBUG ===');
        if (incorrectQuestions.length === 0 && partialQuestions.length === 0) {
            return '';
        }

        const ErrorAnalysisT = t("Error Analysis");
        const undMisText = t("Understanding your mistakes helps you learn better")
        let analysisHTML = `
            <div class="error-analysis-section">
                <h3 class="error-analysis-title">
                    <span style="color: #ef4444;">📊</span> ${ErrorAnalysisT}
                </h3>
                <p class="error-analysis-subtitle">${undMisText}</p>
        `;
        // Analyze patterns
        const patterns = analyzeErrorPatterns(incorrectQuestions, partialQuestions);
        if (patterns.length > 0) {
            const areasImpr = t("Key Areas for Improvement")
            analysisHTML += `
                <div class="pattern-insights">
                    <h4>${areasImpr}:</h4>
                    <ul>
            `;
            patterns.forEach(pattern => {
                analysisHTML += `<li>${pattern}</li>`;
            });
            analysisHTML += `</ul></div>`;
        }
        // Show specific errors
        if (incorrectQuestions.length > 0) {
            analysisHTML += `
                <div class="error-details">
                    <h4 style="color: #ef4444;">${IncorrectAnsw} (${incorrectQuestions.length})</h4>
                    <div class="error-cards">
            `;
            const correctAnswer = t("Correct answer:");
            incorrectQuestions.forEach(item => {
                const yourAnswerT = t("Your answer:");
                analysisHTML += `
                    <div class="error-card incorrect" onclick="window.reviewSpecificQuestion(${item.index})">
                        <div class="error-card-header">
                            <span class="question-num">${questionT} ${item.index + 1}</span>
                            <span class="error-type">${item.question.type === 'yesNo' ? 'Yes/No' : item.question.type === 'textVoice' ? 'Text/Voice' : 'Drag & Drop'}</span>
                        </div>
                        <div class="error-card-content">
                            <p class="question-text">${item.question.text || 'Fill in the blanks question'}</p>
                            <div class="answer-comparison">
                                <div class="your-answer-box">
                                    <strong>${yourAnswerT}</strong> ${formatUserAnswer(item.userAnswer, item.question.type)}
                                </div>
                                <div class="correct-answer-box">
                                    <strong>${correctAnswer}</strong> ${formatCorrectAnswer(item.question, item.userAnswer)}
                                </div>
                            </div>
                        </div>
                    </div>
                `;
            });
            analysisHTML += `</div></div>`;
        }
        if (partialQuestions.length > 0) {
            analysisHTML += `
                <div class="error-details">
                    <h4 style="color: #f59e0b;">${partlyCorrect} (${partialQuestions.length})</h4>
                    <div class="error-cards">
            `;
            partialQuestions.forEach(item => {
                analysisHTML += `
                    <div class="error-card partial" onclick="window.reviewSpecificQuestion(${item.index})">
                        <div class="error-card-header">
                            <span class="question-num">${questionT} ${item.index + 1}</span>
                            <span class="error-type">${item.question.type === 'dragDrop' ? 'Drag & Drop' : 'Text/Voice'}</span>
                        </div>
                        <div class="error-card-content">
                            <p class="question-text">${item.question.text || 'Fill in the blanks question'}</p>
                            <p class="partial-note">You were on the right track but needed more precision.</p>
                        </div>
                    </div>
                `;
            });
            analysisHTML += `</div></div>`;
        }
        analysisHTML += `</div>`;
        return analysisHTML;
    };

    const analyzeErrorPatterns = (incorrectQuestions, partialQuestions) => {
        const patterns = [];
        const allErrors = [...incorrectQuestions, ...partialQuestions];
        // Count error types
        const errorTypes = {};
        let totalAttempted = 0;
        allErrors.forEach(item => {
            errorTypes[item.question.type] = (errorTypes[item.question.type] || 0) + 1;
        });
        // Count total questions by type
        testQuestions.forEach(q => {
            if (answers[testQuestions.indexOf(q)] !== undefined) {
                totalAttempted++;
            }
        });
        // Generate insights
        if (errorTypes.yesNo >= 2) {
            patterns.push(t("Focus on careful reading - multiple Yes/No questions were missed. Pay attention to keywords like 'not', 'only', or 'all'."));
        }
        if (errorTypes.dragDrop >= 2) {
            patterns.push(t("Practice word order and grammar structures - drag & drop questions show gaps in understanding sentence construction."));
        }
        if (errorTypes.textVoice >= 2) {
            patterns.push(t("Work on expressing complete thoughts - text/voice answers need more detail and accuracy."));
        }
        // General advice based on total errors
        const totalErrors = allErrors.length;
        const errorRate = (totalErrors / totalAttempted) * 100;
        if (errorRate >= 60) {
            patterns.push(t("Consider reviewing the material more thoroughly before attempting again. Take notes on key concepts."));
        } else if (errorRate >= 40) {
            patterns.push(t("You're showing good understanding but need to refine some details. Focus on the specific areas mentioned above."));
        }
        return patterns;
    };

    const formatUserAnswer = (answer, type) => {
        if (type === 'yesNo') {
            return answer ? answer.toUpperCase() : t('No answer');
        } else if (type === 'dragDrop') {
            if (Array.isArray(answer)) {
                return `"${answer.join(' ___ ')}"`;
            } else if (typeof answer === 'object' && answer !== null) {
                // New word order format - extract words from object
                const words = Object.values(answer).map(wordId => {
                    // Find the actual word content from the question's draggableWords
                    const question = testQuestions.find((q, idx) => {
                        const userAns = answers[idx];
                        return userAns === answer;
                    });
                    if (question && question.draggableWords) {
                        const wordObj = question.draggableWords.find(w => w.word_id === wordId);
                        return wordObj ? wordObj.content : wordId;
                    }
                    return wordId;
                });
                return words.join(' • ');
            }
            return t('No answer provided');
        } else if (typeof answer === 'string') {
            return answer;
        } else {
            return t('No answer provided');
        }
    };

    const formatCorrectAnswer = (question, userAnswer = null) => {
        if (question.type === 'yesNo') {
            // If we have the correct answer from the question, use it
            if (question.correctAnswer) {
                return question.correctAnswer.toUpperCase();
            }
            // Otherwise, if user answered wrong, show the opposite
            if (userAnswer) {
                return userAnswer.toLowerCase() === 'yes' ? 'NO' : 'YES';
            }
            return t('N/A');
        } else if (question.type === 'dragDrop') {
            return question.correctPositions ? `"${question.correctPositions.join(' ___ ')}"` : t('See explanation');
        } else {
            return question.sampleAnswer || t('See explanation');
        }
    };

    const reviewSpecificQuestion = (index) => {
        setIsReviewMode(true);
        setCurrentQuestion(index);
        setShowingResults(false); // Hide results when reviewing specific question
        const container = document.getElementById('questionContainer');
        if (container) {
            container.innerHTML = `
                <div class="review-mode-header">
                    <h2>${t("Review Your Answer")}</h2>
                    <p>${t("See your answer and the explanation for this question")}</p>
                    <button class="back-to-results-btn" onclick="window.showResults()">
                        ${t("← Back to Results")}
                    </button>
                </div>
            `;
            // Show the specific question in review mode
            showQuestionReview(index);
        }
    };

    const displayResults = (correctCount, partialCount, incorrectCount, percentage) => {

        window.scrollTo({ top: 0, behavior: 'smooth' });

        for (let i = 1; i <= testQuestions.length; i++) {
            const circle = document.getElementById(`circle${i}`);
            if (circle) {
                circle.classList.remove('active');
            }
        }

        let emoji, title, message;
        if (percentage >= 80) {
            emoji = '🏆';
            title = t('Outstanding!');
            message = t("You've mastered this material! Your understanding is exceptional.");
        } else if (percentage >= 60) {
            emoji = '🌟';
            title = t('Great Job!');
            message = t("You're doing really well! Keep up the excellent work.");
        } else if (percentage >= 40) {
            emoji = '💪';
            title = t('Good Effort!');
            message = t("You're making progress! A little more practice will help you improve.");
        } else {
            emoji = '🌱';
            title = t('Keep Growing!');
            message = t("Every expert was once a beginner. Keep practicing and you'll get there!");
        }

        // Generate error analysis
        let errorAnalysisHTML = '';
        if (incorrectCount > 0 || partialCount > 0) {
            errorAnalysisHTML = generateErrorAnalysis();
        }

        const analysisSee = t("Scroll down to see your detailed error analysis")
        // Add a note about error analysis if there are errors
        const analysisNote = (incorrectCount > 0 || partialCount > 0) ?
            `<p style="color: #667eea; margin-top: 20px; font-weight: 600;">📊 ${analysisSee}</p>` : '';

        const container = document.getElementById('questionContainer');
        const tryAgain = t("Try Again");
        const attemptsText = t("You've completed your");
        if (container) {
            container.innerHTML = `
        <div class="results-container">
          <div class="results-header">
            <div class="results-emoji">${emoji}</div>
            <h2 class="results-title">${title}</h2>
          </div>
          
          <div class="score-circles">
            <div class="score-circle correct">
              <div class="score-value">${correctCount}</div>
              <div class="score-label">${t("Correct")}</div>
            </div>
            <div class="score-circle partial">
              <div class="score-value">${partialCount}</div>
              <div class="score-label">${t("Partial")}</div>
            </div>
            <div class="score-circle incorrect">
              <div class="score-value">${incorrectCount}</div>
              <div class="score-label">${t("Incorrect")}</div>
            </div>
          </div>
          
          <div class="motivational-message">
            <p>${message}</p>
            ${analysisNote}
          </div>
          
          ${errorAnalysisHTML}
          
          <div style="display: flex; gap: 20px; justify-content: center; margin-top: 30px;">
            ${attempts < maxAttempts ? `
              <button class="restart-btn" onclick="window.resetTest()">${tryAgain}</button>
            ` : ''}
          </div>
          
          ${attempts >= maxAttempts ? `
            <p style="color: #6b7280; margin-top: 20px;">${attemptsText} ${maxAttempts} ${t("attempts")}.</p>
          ` : ''}
        </div>
      `;
        }

        onTestComplete({
            correctCount,
            partialCount,
            incorrectCount,
            percentage,
            answers,
            questionResults,
            attempts
        });

        // Check completion after test is finished
        // Wait for backend to process test results
        if (onActivityComplete) {
            setTimeout(() => {
                onActivityComplete();
            }, 2000); // Increased to 2 seconds for safety
        }
    };

    const resetTest = () => {
        setCurrentQuestion(0);
        setAnswers({});
        setQuestionResults({});
        setBackendFeedback({}); // Clear backend feedback when resetting
        setTestStarted(false);
        setIsReviewMode(false);
        setShowProgressPanel(false);
        setShowLandingScreen(true);
        setShowQuestionContainer(false);
        setTestCompleted(false);
        setShowingResults(false);

        // Reset progress circles and line
        for (let i = 1; i <= 5; i++) {
            const circle = document.getElementById(`circle${i}`);
            if (circle) {
                circle.classList.remove('completed', 'incorrect', 'partial', 'active');
            }
        }
        const progressLine = document.querySelector('.progress-line');
        if (progressLine) {
            progressLine.style.setProperty('--progress', '0%');
        }
    };

    const reviewAnswers = () => {
        setIsReviewMode(true);
        setCurrentQuestion(0);
        showQuestionReview(0);
    };

    const showQuestionReview = (index) => {
        const container = document.getElementById('questionContainer');
        if (container) {
            container.innerHTML = `
                <div class="review-mode-header">
                    <h2>${t("Review Mode")}</h2>
                    <p>${t("Review all your answers and explanations")}</p>
                    <button class="back-to-results-btn" onclick="window.showResults()">
                        ${t("← Back to Results")}
                    </button>
                </div>
            `;
            // Show the question with answer already filled
            const tempContainer = document.createElement('div');
            tempContainer.innerHTML = container.innerHTML;
            // Recreate the question in review mode
            showQuestion(index);
            // Auto-fill and check the answer
            const question = testQuestions[index];
            if (question.type === 'yesNo' && answers[index]) {
                const radio = document.getElementById(`${answers[index]}${index}`);
                if (radio) {
                    radio.checked = true;
                    radio.disabled = true;
                }
                document.querySelectorAll(`input[name="q${index}"]`).forEach(input => {
                    input.disabled = true;
                });
                // Show explanation using stored results
                const isCorrect = questionResults[index] === 'correct';
                const isPartial = questionResults[index] === 'partial';
                showExplanation(index, isCorrect, isPartial, answers[index], true);
            } else if (question.type === 'textVoice' && answers[index]) {
                const input = document.getElementById(`textAnswer${index}`);
                if (input) {
                    input.value = answers[index];
                    input.readOnly = true;
                    // Auto-resize textarea if it contains content
                    if (input.tagName.toLowerCase() === 'textarea') {
                        setTimeout(() => autoResizeTextarea(input), 0);
                    }
                }
                // Show explanation using stored results
                const isCorrect = questionResults[index] === 'correct';
                const isPartial = questionResults[index] === 'partial';
                showExplanation(index, isCorrect, isPartial, answers[index], true);
            } else if (question.type === 'dragDrop' && answers[index]) {
                // Fill the gaps
                answers[index].forEach((word, i) => {
                    const gap = document.getElementById(`gap${index}_${i}`);
                    if (gap) {
                        gap.textContent = word;
                        gap.classList.add('filled');
                    }
                });
                // Disable dragging
                document.querySelectorAll('.draggable-word').forEach(word => {
                    word.draggable = false;
                    word.classList.add('used');
                });
                // Show explanation using stored results
                const isCorrect = questionResults[index] === 'correct';
                const isPartial = questionResults[index] === 'partial';
                showExplanation(index, isCorrect, isPartial, answers[index], true);
            }
            // Add navigation buttons at the bottom
            const navHTML = `
                <div class="action-buttons" style="margin-top: 40px;">
                    ${index > 0 ? `<button class="action-btn" onclick="window.showQuestionReview(${index - 1})">← ${t("Previous")}</button>` : ''}
                    ${index < testQuestions.length - 1 ? `<button class="action-btn next-btn" onclick="window.showQuestionReview(${index + 1})">${t("Next")} →</button>` : ''}
                </div>
            `;
            container.innerHTML += navHTML;
        }
    };



    // Drag and Drop functions
    const allowDrop = (ev) => {
        ev.preventDefault();
        ev.target.classList.add('drag-over');
    };

    const removeDragOver = (ev) => {
        ev.target.classList.remove('drag-over');
    };

    const drag = (ev) => {
        ev.dataTransfer.setData("text", ev.target.id);
        ev.target.classList.add('dragging');
    };

    const returnWordToBank = (questionIndex, gapIndex) => {
        // Prevent returning words if question is already answered
        if (questionResults[questionIndex]) {
            return;
        }

        const gap = document.getElementById(`gap${questionIndex}_${gapIndex}`);
        if (!gap || !gap.textContent.trim()) {
            return; // Gap is already empty
        }

        const word = gap.textContent.trim();
        const wordElement = document.querySelector(`[data-word="${word}"]`);

        if (wordElement) {
            // Return word to bank
            wordElement.classList.remove('used');
            wordElement.draggable = true;
            wordElement.style.opacity = '1';

            // Clear the gap
            gap.textContent = '';
            gap.classList.remove('filled');
        }
    };

    const dropAndCheck = (ev, questionIndex, gapIndex) => {
        // Prevent re-answering if question is already answered
        if (questionResults[questionIndex]) {
            return;
        }

        ev.preventDefault();
        ev.target.classList.remove('drag-over');

        const data = ev.dataTransfer.getData("text");
        const draggedElement = document.getElementById(data);
        const word = draggedElement.getAttribute('data-word');
        const targetGap = ev.target;

        // Check if the gap is already filled
        if (targetGap.textContent.trim() !== '') {
            // If gap is already filled, return the current word to the word bank
            const currentWord = targetGap.textContent.trim();
            const currentWordElement = document.querySelector(`[data-word="${currentWord}"]`);
            if (currentWordElement) {
                currentWordElement.classList.remove('used');
                currentWordElement.draggable = true;
                currentWordElement.style.opacity = '1';
            }
        }

        // Place the new word in the gap
        targetGap.textContent = word;
        targetGap.classList.add('filled');

        // Mark the dragged word as used
        draggedElement.classList.remove('dragging');
        draggedElement.classList.add('used');
        draggedElement.draggable = false;
        draggedElement.style.opacity = '0.5';

        const question = testQuestions[questionIndex];
        const totalGaps = question.correctPositions.length;
        let filledGaps = 0;

        for (let i = 0; i < totalGaps; i++) {
            const gap = document.getElementById(`gap${questionIndex}_${i}`);
            if (gap && gap.textContent.trim()) {
                filledGaps++;
            }
        }

        if (filledGaps === totalGaps) {
            setTimeout(() => {
                checkDragDropAnswer(questionIndex);
            }, 300);
        }
    };

    // New word order drag & drop functions
    const dropAndCheckWordOrder = (ev, questionIndex, gapId) => {
        // Prevent re-answering if question is already answered
        if (questionResults[questionIndex]) {
            return;
        }

        ev.preventDefault();
        ev.target.classList.remove('drag-over');

        const data = ev.dataTransfer.getData("text");
        const draggedElement = document.getElementById(data);
        
        if (!draggedElement) {
            console.error('Dragged element not found:', data);
            return;
        }

        const word = draggedElement.getAttribute('data-word');
        const wordId = draggedElement.getAttribute('data-word-id');
        const targetGap = ev.target;

        // Check if the gap is already filled
        if (targetGap.textContent.trim() !== '') {
            // If gap is already filled, return the current word to the word bank
            const currentWordId = targetGap.getAttribute('data-word-id');
            if (currentWordId) {
                // Find the word element in the draggable words section
                const currentWordElement = document.querySelector(`[data-word-id="${currentWordId}"]`);
                if (currentWordElement) {
                    currentWordElement.classList.remove('used');
                    currentWordElement.draggable = true;
                    currentWordElement.style.opacity = '1';
                    currentWordElement.style.pointerEvents = 'auto';
                    currentWordElement.style.cursor = 'grab';
                }
            }
        }

        // Place the new word in the gap
        targetGap.textContent = word;
        targetGap.setAttribute('data-word-id', wordId);
        targetGap.classList.add('filled');

        // Handle the dragged element based on where it came from
        if (draggedElement.classList.contains('word-gap')) {
            // Word was dragged from another gap - clear the source gap
            draggedElement.textContent = '';
            draggedElement.removeAttribute('data-word-id');
            draggedElement.classList.remove('filled');
        } else {
            // Word was dragged from the word bank - mark as used
            draggedElement.classList.remove('dragging');
            draggedElement.classList.add('used');
            draggedElement.draggable = false;
            draggedElement.style.opacity = '0.5';
            draggedElement.style.pointerEvents = 'none';
            draggedElement.style.cursor = 'not-allowed';
        }

        // Check if all gaps are filled
        const question = testQuestions[questionIndex];
        if (question.templateSegments) {
            const totalGaps = question.templateSegments.filter(s => s.type === 'gap').length;
            let filledGaps = 0;

            question.templateSegments.forEach(segment => {
                if (segment.type === 'gap') {
                    const gap = document.getElementById(`gap${questionIndex}_${segment.gap_id}`);
                    if (gap && gap.textContent.trim()) {
                        filledGaps++;
                    }
                }
            });

            if (filledGaps === totalGaps) {
                setTimeout(() => {
                    checkAnswer(questionIndex);
                }, 300);
            }
        }
    };

    const returnWordToBankWordOrder = (questionIndex, gapId) => {
        // Prevent returning words if question is already answered
        if (questionResults[questionIndex]) {
            return;
        }

        const gap = document.getElementById(`gap${questionIndex}_${gapId}`);
        if (!gap || !gap.textContent.trim()) {
            return; // Gap is already empty
        }

        // Update the React state to remove the word from this gap
        const currentAnswer = answers[questionIndex] || {};
        const updatedAnswer = { ...currentAnswer };
        delete updatedAnswer[gapId]; // Remove the word from this gap
        
        // Update the answers state to trigger a re-render
        // The re-render will automatically update the word states correctly
        setAnswers(prev => ({
            ...prev,
            [questionIndex]: updatedAnswer
        }));
    };

    const dragWordOrder = (ev) => {
        ev.dataTransfer.setData("text", ev.target.id);
        ev.target.classList.add('dragging');
    };

    const checkDragDropAnswer = (index) => {
        // Prevent re-answering if question is already answered
        if (questionResults[index]) {
            return;
        }

        const question = testQuestions[index];
        const gaps = [];
        let correctCount = 0;

        for (let i = 0; i < question.correctPositions.length; i++) {
            const gap = document.getElementById(`gap${index}_${i}`);
            const word = gap?.textContent.trim() || '';
            gaps.push(word);
            if (word === question.correctPositions[i]) {
                correctCount++;
            }
        }

        const isCorrect = correctCount === question.correctPositions.length;
        const isPartial = correctCount > 0 && correctCount < question.correctPositions.length;

        const newAnswers = { ...answers, [index]: gaps };
        const newResults = { ...questionResults, [index]: isCorrect ? 'correct' : (isPartial ? 'partial' : 'incorrect') };

        setAnswers(newAnswers);
        setQuestionResults(newResults);

        const circle = document.getElementById(`circle${index + 1}`);
        if (circle) {
            circle.classList.remove('active');

            if (isCorrect) {
                circle.classList.add('completed');
            } else if (isPartial) {
                circle.classList.add('partial');
            } else {
                circle.classList.add('incorrect');
            }
        }

        updateProgressLine();
        showExplanation(index, isCorrect, isPartial, gaps);

        // Disable all draggable words for this question
        document.querySelectorAll(`[id^="word${index}_"]`).forEach(word => {
            if (word) {
                word.draggable = false;
                word.classList.add('used');
            }
        });

        // Disable all gaps for this question
        document.querySelectorAll(`[id^="gap${index}_"]`).forEach(gap => {
            if (gap) {
                gap.removeAttribute('ondrop');
                gap.removeAttribute('ondragover');
                gap.removeAttribute('ondragleave');
            }
        });

        showFeedback(isCorrect, isPartial, isCorrect, newResults);
    };

    // Effect to show question when currentQuestion changes
    useEffect(() => {
        if (testStarted && showQuestionContainer && testQuestions.length > 0 && !isReviewMode) {
            showQuestion(currentQuestion);
        }
    }, [currentQuestion, testStarted, showQuestionContainer, testQuestions, isReviewMode]);

    // Voice recording functions
    const toggleRecording = async (index) => {
        console.log('toggleRecording called for index:', index, 'isRecording:', isRecording);

        // Simple state check - if we're recording, stop; otherwise start
        if (isRecording || mediaRecorderRef.current) {
            console.log('Stopping recording...');
            stopRecording(index);
        } else {
            console.log('Starting recording...');
            await startRecording(index);
        }
    };

    const startRecording = async (index) => {
        console.log('Starting recording for index:', index);

        // Reset manual stop flag
        isManualStopRef.current = false;

        try {
            // Check if browser supports Speech Recognition
            const SpeechRecognition = window.SpeechRecognition || window.webkitSpeechRecognition;

            if (!SpeechRecognition) {
                console.warn('Speech Recognition not supported, falling back to MediaRecorder');
                await startMediaRecording(index);
                return;
            }

            // Use Speech Recognition API for real transcription
            const recognition = new SpeechRecognition();

            // Configure speech recognition BEFORE storing reference
            recognition.continuous = true;
            recognition.interimResults = true;
            // Force English language for now to avoid Ukrainian/Russian detection
            recognition.lang = 'en-US';
            recognition.maxAlternatives = 1;

            let finalTranscript = '';
            let interimTranscript = '';

            recognition.onstart = () => {
                console.log('Speech recognition started successfully');
                setIsRecording(true);

                // Update UI
                const voiceBtn = document.getElementById(`voiceBtn${index}`);
                const voiceStatus = document.getElementById(`voiceStatus${index}`);

                if (voiceBtn) {
                    voiceBtn.innerHTML = '⏹';
                    voiceBtn.classList.add('recording');
                    console.log('Button updated to recording state');
                }

                if (voiceStatus) {
                    voiceStatus.textContent = t("Recording...");
                }
            };

            recognition.onresult = (event) => {
                // Skip processing if manually stopped
                if (isManualStopRef.current) {
                    console.log('Skipping onresult - manual stop detected');
                    return;
                }

                interimTranscript = '';

                for (let i = event.resultIndex; i < event.results.length; i++) {
                    const transcript = event.results[i][0].transcript;

                    if (event.results[i].isFinal) {
                        finalTranscript += transcript + ' ';
                        console.log('Final transcript:', transcript);
                    } else {
                        interimTranscript += transcript;
                        console.log('Interim transcript:', transcript);
                    }
                }

                // Update textarea with real-time transcription
                const textarea = document.getElementById(`textAnswer${index}`);
                if (textarea) {
                    const displayText = finalTranscript + interimTranscript;
                    textarea.value = displayText;
                    autoResizeTextarea(textarea);
                    console.log('Updated textarea with:', displayText);
                }
            };

            recognition.onerror = (event) => {
                console.error('Speech recognition error:', event.error);

                // Skip error handling if manually stopped (abort is expected)
                if (isManualStopRef.current && event.error === 'aborted') {
                    console.log('Ignoring abort error - was manually stopped');
                    return;
                }

                // Handle specific errors
                if (event.error === 'not-allowed') {
                    alert(t('Microphone access denied. Please allow microphone access and try again.'));
                } else if (event.error === 'no-speech') {
                    console.log('No speech detected, continuing...');
                    return; // Don't stop on no-speech
                }

                // Update UI on error
                const voiceStatus = document.getElementById(`voiceStatus${index}`);
                if (voiceStatus) {
                    voiceStatus.textContent = t("Error - try again");
                }

                setIsRecording(false);
                mediaRecorderRef.current = null;

                // Reset button
                const voiceBtn = document.getElementById(`voiceBtn${index}`);
                if (voiceBtn) {
                    voiceBtn.innerHTML = '🎤';
                    voiceBtn.classList.remove('recording');
                }
            };

            recognition.onend = () => {
                console.log('Speech recognition ended');
                console.log('Final complete transcript:', finalTranscript.trim());

                // Skip UI updates if manually stopped
                if (isManualStopRef.current) {
                    console.log('Manual stop detected - skipping UI update in onend');

                    // Still ensure final transcript is preserved
                    const textarea = document.getElementById(`textAnswer${index}`);
                    if (textarea && finalTranscript.trim()) {
                        textarea.value = finalTranscript.trim();
                        autoResizeTextarea(textarea);
                        console.log('Final transcript preserved after manual stop:', finalTranscript.trim());
                    }
                    return;
                }

                // Natural end - update UI
                console.log('Natural end - updating UI');
                setIsRecording(false);

                // Update UI
                const voiceBtn = document.getElementById(`voiceBtn${index}`);
                const voiceStatus = document.getElementById(`voiceStatus${index}`);

                if (voiceBtn) {
                    voiceBtn.innerHTML = '🎤';
                    voiceBtn.classList.remove('recording');
                }

                if (voiceStatus) {
                    voiceStatus.textContent = t("Ready");
                }

                // Show clear button
                const clearBtn = document.getElementById(`clearBtn${index}`);
                if (clearBtn) {
                    clearBtn.style.display = 'inline-block';
                }

                // Ensure final transcript is in textarea
                const textarea = document.getElementById(`textAnswer${index}`);
                if (textarea && finalTranscript.trim()) {
                    textarea.value = finalTranscript.trim();
                    autoResizeTextarea(textarea);
                    console.log('Final transcript set in textarea:', finalTranscript.trim());
                }

                // Clear the reference
                mediaRecorderRef.current = null;
            };

            // Store reference AFTER setting up all event handlers
            mediaRecorderRef.current = recognition;

            // Start speech recognition
            console.log('Starting Speech Recognition...');
            recognition.start();

        } catch (error) {
            console.error('Error starting speech recognition:', error);
            setIsRecording(false);
            mediaRecorderRef.current = null;
            alert(t('Could not access microphone. Please check your permissions.'));
        }
    };

    // Fallback MediaRecorder function for browsers without Speech Recognition
    const startMediaRecording = async (index) => {
        console.log('Starting MediaRecorder fallback for index:', index);

        try {
            const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
            streamRef.current = stream;

            const mediaRecorder = new MediaRecorder(stream);
            mediaRecorderRef.current = mediaRecorder;

            const chunks = [];

            mediaRecorder.ondataavailable = (event) => {
                if (event.data.size > 0) {
                    chunks.push(event.data);
                }
            };

            mediaRecorder.onstop = () => {
                console.log('MediaRecorder stopped - using fallback transcription');

                const blob = new Blob(chunks, { type: 'audio/wav' });
                console.log('Audio blob created:', blob.size, 'bytes');

                // Fallback transcription message
                const fallbackMessage = t("Voice recorded successfully. Speech recognition not available in this browser.");

                // Put fallback message in textarea
                const textarea = document.getElementById(`textAnswer${index}`);
                if (textarea) {
                    textarea.value = fallbackMessage;
                    autoResizeTextarea(textarea);
                }

                // Update voice status
                const voiceStatus = document.getElementById(`voiceStatus${index}`);
                if (voiceStatus) {
                    voiceStatus.textContent = t("Ready");
                }

                // Show clear button
                const clearBtn = document.getElementById(`clearBtn${index}`);
                if (clearBtn) {
                    clearBtn.style.display = 'inline-block';
                }

                // Clean up
                if (streamRef.current) {
                    streamRef.current.getTracks().forEach(track => track.stop());
                    streamRef.current = null;
                }

                setIsRecording(false);
            };

            // Start recording
            mediaRecorder.start();
            setIsRecording(true);

            // Update UI
            const voiceBtn = document.getElementById(`voiceBtn${index}`);
            const voiceStatus = document.getElementById(`voiceStatus${index}`);

            if (voiceBtn) {
                voiceBtn.innerHTML = '⏹';
                voiceBtn.classList.add('recording');
            }

            if (voiceStatus) {
                voiceStatus.textContent = t("Recording...");
            }

        } catch (error) {
            console.error('Error starting MediaRecorder:', error);
            setIsRecording(false);
            alert(t('Could not access microphone. Please check your permissions.'));
        }
    };

    const stopRecording = (index) => {
        console.log('Stopping recording for index:', index);

        // Set manual stop flag to prevent race conditions
        isManualStopRef.current = true;

        // Update state immediately to prevent new recordings
        setIsRecording(false);

        // Update UI immediately
        const voiceBtn = document.getElementById(`voiceBtn${index}`);
        const voiceStatus = document.getElementById(`voiceStatus${index}`);

        if (voiceBtn) {
            voiceBtn.innerHTML = '🎤';
            voiceBtn.classList.remove('recording');
            console.log('Button updated to microphone state');
        }

        if (voiceStatus) {
            voiceStatus.textContent = t("Processing...");
        }

        // Handle Speech Recognition or MediaRecorder cleanup
        if (mediaRecorderRef.current) {
            try {
                console.log('Manually stopping Speech Recognition with abort()');

                // For Speech Recognition, use abort() to immediately terminate
                if (typeof mediaRecorderRef.current.abort === 'function') {
                    mediaRecorderRef.current.abort();
                } else if (typeof mediaRecorderRef.current.stop === 'function') {
                    mediaRecorderRef.current.stop();
                }
            } catch (error) {
                console.error('Error stopping recording:', error);
            }

            // Clear the reference immediately
            mediaRecorderRef.current = null;
        }

        // Clean up stream if using MediaRecorder fallback
        if (streamRef.current) {
            console.log('Stopping stream tracks');
            try {
                streamRef.current.getTracks().forEach(track => track.stop());
            } catch (error) {
                console.error('Error stopping stream tracks:', error);
            }
            streamRef.current = null;
        }

        // Ensure final UI state
        setTimeout(() => {
            const voiceStatus = document.getElementById(`voiceStatus${index}`);
            const clearBtn = document.getElementById(`clearBtn${index}`);

            if (voiceStatus) {
                voiceStatus.textContent = t("Ready");
            }

            // Show clear button if there's text in the textarea
            const textarea = document.getElementById(`textAnswer${index}`);
            if (clearBtn && textarea && textarea.value.trim()) {
                clearBtn.style.display = 'inline-block';
            }

            // Reset manual stop flag after UI is updated
            isManualStopRef.current = false;
        }, 200);
    };

    const clearVoiceInput = (index) => {
        const textarea = document.getElementById(`textAnswer${index}`);
        if (textarea) {
            textarea.value = '';
            autoResizeTextarea(textarea);
        }

        const clearBtn = document.getElementById(`clearBtn${index}`);
        if (clearBtn) {
            clearBtn.style.display = 'none';
        }
    };

    const autoResizeTextarea = (textarea) => {
        if (textarea && textarea.tagName.toLowerCase() === 'textarea') {
            textarea.style.height = 'auto';
            textarea.style.height = Math.max(textarea.scrollHeight, 25) + 'px';
        }
    };

    // Make functions available globally for onclick handlers
    useEffect(() => {
        window.resetTest = resetTest;
        window.showResults = showResults;
        window.nextQuestion = nextQuestion;
        window.toggleHint = toggleHint;
        window.instantCheckYesNo = instantCheckYesNo;
        window.toggleRecording = toggleRecording;
        window.clearVoiceInput = clearVoiceInput;
        window.allowDrop = allowDrop;
        window.removeDragOver = removeDragOver;
        window.drag = drag;
        window.dropAndCheck = dropAndCheck;
        window.checkAnswer = checkAnswer;
        window.reviewAnswers = reviewAnswers;
        window.showQuestionReview = showQuestionReview;
        window.reviewSpecificQuestion = reviewSpecificQuestion;
        window.returnWordToBank = returnWordToBank;
        window.dropAndCheckWordOrder = dropAndCheckWordOrder;
        window.returnWordToBankWordOrder = returnWordToBankWordOrder;
        window.dragWordOrder = dragWordOrder;

        return () => {
            delete window.resetTest;
            delete window.showResults;
            delete window.nextQuestion;
            delete window.toggleHint;
            delete window.instantCheckYesNo;
            delete window.toggleRecording;
            delete window.clearVoiceInput;
            delete window.allowDrop;
            delete window.removeDragOver;
            delete window.drag;
            delete window.dropAndCheck;
            delete window.checkAnswer;
            delete window.reviewAnswers;
            delete window.showQuestionReview;
            delete window.reviewSpecificQuestion;
            delete window.returnWordToBank;
            delete window.dropAndCheckWordOrder;
            delete window.returnWordToBankWordOrder;
            delete window.dragWordOrder;
        };
    }, [testQuestions, answers, questionResults, currentQuestion, attempts, maxAttempts]);



    // Add input event listener to show/hide clear button and auto-resize textarea
    useEffect(() => {
        const handleInput = (e) => {
            if (e.target.classList.contains('text-input-field')) {
                const index = e.target.id.replace('textAnswer', '');
                const clearBtn = document.getElementById(`clearBtn${index}`);
                if (clearBtn) {
                    clearBtn.style.display = e.target.value.trim() ? 'flex' : 'none';
                }

                // Auto-resize textarea if it's a textarea element
                if (e.target.tagName.toLowerCase() === 'textarea') {
                    autoResizeTextarea(e.target);
                }
            }
        };

        document.addEventListener('input', handleInput);

        return () => {
            document.removeEventListener('input', handleInput);
        };
    }, []);

    return (
        <div className="app-container">
            {/* Animated Background Elements 
            <div className="floating-shape shape-1"></div>
            <div className="floating-shape shape-2"></div>
            <div className="floating-shape shape-3"></div>
            <div className="floating-shape shape-4"></div>
            <div className="floating-shape shape-5"></div>

            <div className="particle particle-1"></div>
            <div className="particle particle-2"></div>
            <div className="particle particle-3"></div>
            */}

            {/* Left Working Area */}
            <div className="working-area">
                {/* Progress Panel */}
                {showProgressPanel && (
                    <div className="progress-panel" id="progressPanel">
                        <div className="progress-panel-content">
                            <h1 className="title title-check">{t("Check Understanding")}</h1>
                            <p className="progress-subtitle">{t("Click any question to jump directly to it!")}</p>
                            <div className="progress-container">
                                <div className="progress-line"></div>
                                {Array.from({ length: testQuestions.length }, (_, i) => i + 1).map(num => (
                                    <div
                                        key={num}
                                        className="progress-circle"
                                        id={`circle${num}`}
                                        onClick={() => goToQuestion(num - 1)}
                                    >
                                        {num}
                                    </div>
                                ))}
                            </div>
                            {testCompleted && !showingResults && (
                                <div style={{ textAlign: 'center', marginTop: '20px' }}>
                                    <button
                                        className="action-btn review-btn"
                                        onClick={() => window.showResults()}
                                        style={{
                                            background: '#667eea',
                                            color: 'white',
                                            border: 'none',
                                            padding: '10px 20px',
                                            borderRadius: '8px',
                                            cursor: 'pointer',
                                            fontSize: '14px',
                                            fontWeight: '600'
                                        }}
                                    >
                                        📊 {t("View Results")}
                                    </button>
                                </div>
                            )}
                        </div>
                    </div>
                )}        <div className="container">
                    {/* Landing Screen */}
                    {showLandingScreen && (
                        <div className="landing-screen" id="landingScreen">
                            <h1 className="landing-title">{t("Master Your English!")}</h1>

                            {isLoadingQuestions ? (
                                <div className="loading-questions">
                                    <div className="loading-spinner"></div>
                                    <p>{t("Loading questions...")}</p>
                                    <p style={{ fontSize: '0.9em', color: '#6b7280' }}>
                                        {t("This may take 5-10 seconds for new content")}
                                    </p>
                                </div>
                            ) : (
                                <>
                                    <button className="start-testing-btn" onClick={startTesting}>
                                        {t("Start Testing")}
                                    </button>

                                    <div className="test-type-selection">
                                        <p style={{ color: '#6b7280', marginBottom: '20px', fontSize: '1.1em' }}>
                                            {t("Choose your challenge:")}
                                        </p>
                                        <label className="test-type-label">
                                            <input
                                                type="checkbox"
                                                checked={testTypes.yesNo}
                                                onChange={(e) => setTestTypes({ ...testTypes, yesNo: e.target.checked })}
                                            />
                                            <span>❓ {t("Yes/No Questions")}</span>
                                        </label>
                                        <label className="test-type-label">
                                            <input
                                                type="checkbox"
                                                checked={testTypes.textVoice}
                                                onChange={(e) => setTestTypes({ ...testTypes, textVoice: e.target.checked })}
                                            />
                                            <span>🎤 {t("Text/Voice Response")}</span>
                                        </label>
                                        <label className="test-type-label">
                                            <input
                                                type="checkbox"
                                                checked={testTypes.dragDrop}
                                                onChange={(e) => setTestTypes({ ...testTypes, dragDrop: e.target.checked })}
                                            />
                                            <span>🎯 {t("Drag & Drop")}</span>
                                        </label>
                                    </div>

                                    <div className="attempts-counter">
                                        {t("Attempts")}: <span id="attemptsCount">{attempts}</span>/{maxAttempts}
                                    </div>

                                    {showSelectOneType && (
                                        <div className={`oneTypeWarningModal ${isClosingModal ? 'closing' : ''}`}>
                                            <div className="oneTypeWarningContent">
                                            <p>{t('Please select at least one test type!')}</p>
                                            <button className='gotItBtn' onClick={() => {
                                                setIsClosingModal(true);
                                                setTimeout(() => {
                                                    setShowSelectOneType(false);
                                                    setIsClosingModal(false);
                                                }, 300);
                                            }}>
                                                {t("Got it")}
                                            </button>
                                            </div>
                                        </div>
                                    )}
                                </>
                            )}
                        </div>
                    )}

                    {/* Question Container */}
                    {showQuestionContainer && (
                        <div className="question-container" id="questionContainer">
                            {/* Questions will be dynamically inserted here */}
                        </div>
                    )}
                </div>
            </div>
        </div>
    );

};

export default QuestionCard;